import {
  filterLongCategoryName,
  isLongCategoryName,
} from "@faire/deprecated-retailer-visitor-shared/components/Carousel/categoryTileUtils";
import { GhostBlock } from "@faire/deprecated-retailer-visitor-shared/components/GhostLoading/GhostBlock";
import { GridViewBase } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/GridViewBase";
import {
  BaseCarouselProps,
  CarouselBase,
  IProps,
} from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/deprecated-retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { EAEAEA_PIXEL } from "@faire/deprecated-retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { Core } from "@faire/design-tokens";
import { ICategoryTile } from "@faire/web-api--source/indigofair/data/ICategoryTile";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { optimizeImageUrlFillSquare } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { Link } from "@faire/web/ui/LinkAnchor";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import * as React from "react";
import styled from "styled-components";

const IMAGE_WIDTH = 72;
const TILE_WIDTH = 88;

export const AvatarCarousel: React.FC<BaseCarouselProps> = (props) => {
  const CarouselBaseComponent = CarouselBase as React.ComponentType<
    IProps<ICategoryTile>
  >;
  const { onItemClick, layoutElement, requestId, onVisible } =
    React.useContext(Context);
  const { isMobile } = useViewport();
  const renderAvatarTile = (avatarTile: ICategoryTile, index: number) => {
    return (
      <AvatarTile
        avatar={avatarTile}
        key={index}
        eventName={layoutElement.on_click_event_name}
        requestId={requestId}
        onItemClick={(categoryLink) => onItemClick?.(categoryLink, "", "")}
      />
    );
  };

  const getItems = () => {
    return layoutElement.layout_element_data
      .map((layoutElementData) => layoutElementData["category_tile"])
      .filter(isNotUndefined);
  };

  const onItemsVisible = (items: ICategoryTile[]) => {
    const itemTokens = items
      .map((item) => item.category_path_identifier)
      .filter(isNotUndefined);
    if (layoutElement.on_click_event_name) {
      onVisible?.(
        layoutElement.on_click_event_name,
        requestId,
        itemTokens?.join(" ") ?? ""
      );
    }
  };

  return isMobile &&
    layoutElement.type === ILayoutElement.Type.AVATAR_CAROUSEL_WITH_GRID ? (
    <StyledGridViewBase>
      <GridViewBase
        items={getItems()}
        renderItem={renderAvatarTile}
        rows={2}
        itemsPerSlide={3}
        gap={24}
        expandable
        useArrowToggle
      />
    </StyledGridViewBase>
  ) : (
    <CarouselBaseComponent
      {...props}
      onItemsVisible={onItemsVisible}
      items={getItems()}
      renderItem={renderAvatarTile}
      itemGap={16}
      noWrap
    />
  );
};

const AvatarTile: React.FC<{
  avatar: ICategoryTile;
  eventName: string | undefined;
  requestId: string | undefined;
  onItemClick?: (categoryLink: string) => void;
}> = ({ avatar, onItemClick }) => {
  const isSSR = useIsSSR();
  const imageURL = avatar.image
    ? optimizeImageUrlFillSquare(avatar.image, isSSR, IMAGE_WIDTH)
    : EAEAEA_PIXEL;
  const ctaLink = avatar.cta_link;

  if (!ctaLink) {
    return null;
  }
  return (
    <Column align="center" style={{ width: TILE_WIDTH, margin: "0 auto" }}>
      <StyledLink
        to={ctaLink}
        onClick={() => onItemClick?.(ctaLink)}
        data-test-id="avatar-tile-link"
      >
        <ImageContainer>
          <StyledImageOverlay />
          {!avatar.image ? (
            <GhostImage />
          ) : (
            <Image alt={avatar.name} src={imageURL} />
          )}
        </ImageContainer>
        <Spacer size={4} />
        <AvatarName $isLongCategoryName={isLongCategoryName(avatar.name)}>
          {filterLongCategoryName(avatar.name)}
        </AvatarName>
      </StyledLink>
    </Column>
  );
};

const ImageContainer = styled.div`
  position: relative;
  width: ${IMAGE_WIDTH}px;
  height: ${IMAGE_WIDTH}px;
  margin: auto;
`;

const StyledImageOverlay = styled(TransparencyOverlay)`
  border: 1px solid ${Core.surface.muted};
  border-radius: 100%;
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 100%;
`;

const GhostImage = styled(GhostBlock)`
  border-radius: 100%;
`;

const AvatarName = styled(Typography).attrs({
  align: "center",
  maxLines: 3,
  forwardedAs: "p",
})<{
  $isLongCategoryName?: boolean;
}>`
  &&& {
    margin-top: ${getSpacing("1x")};

    ${responsiveMediaQueries.mobile} {
      margin-bottom: ${getSpacing("1x")};
    }

    // manually set max-height to make sure underline animation is visible
    max-height: 60px;
    width: ${TILE_WIDTH}px;
    text-overflow: ellipsis;
    display: ${(props) =>
      props.$isLongCategoryName ? "-webkit-box" : "inline-block"};
    overflow: hidden;
    text-align: center;
  }
`;

const StyledGridViewBase = styled.div`
  ${responsiveMediaQueries.mobile} {
    // to offset the padding of GridViewBase
    margin: ${getSpacing(0, "-2x")};
  }
`;

const StyledLink = styled(Link)`
  &:focus-visible {
    outline: solid;
    outline: revert;
    outline-offset: -1px;
  }
`;
